import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import Notify from '../../../shared/components/widgets/Notify';
import { getBudgetNotes, createBudgetNote, updateBudgetNote } from './budgetApi';

const sortNewestFirst = (notes) => [...notes].sort((a, b) => {
  const diff = moment(b.createdTs).valueOf() - moment(a.createdTs).valueOf();
  return diff !== 0 ? diff : b.id - a.id;
});

/**
 * Loads and manages Budget Notes for an account.
 * - One retailer (e.g. ['google']): fetches that retailer's notes + the 'all' notes.
 * - Several retailers (Multi-Platform): fetches each one, merges and de-duplicates by id.
 * - No retailers: fetches only the 'all' notes.
 * Notes are always returned newest first, and the server's saved version of a note is used.
 *
 * @param {Object} params
 * @param {number} params.accountId - Selected account id
 * @param {Array<string>} params.retailers - Unique retailer keys to include
 * @param {string} params.jwtToken - JWT token
 */
export default function useBudgetNotes({ accountId, retailers, jwtToken }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Stable string so the effect only re-runs when the actual retailer set changes
  const retailersKey = [...new Set(retailers || [])].sort().join(',');

  const loadNotes = useCallback(async () => {
    if (!accountId) {
      setNotes([]);
      return;
    }
    setLoading(true);
    try {
      const keys = retailersKey ? retailersKey.split(',') : ['all'];
      const results = await Promise.all(
        keys.map(retailer => getBudgetNotes({ accountId, retailer, jwtToken })),
      );
      const byId = {};
      results.flat().forEach(note => { byId[note.id] = note; });
      setNotes(sortNewestFirst(Object.values(byId)));
    } catch (error) {
      console.warn('[Budget Notes] load failed:', error.message || error);
      Notify.show('danger', 'Budget Notes', 'Unable to load budget notes.');
    } finally {
      setLoading(false);
    }
  }, [accountId, retailersKey, jwtToken]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const addNote = useCallback(async ({ note, retailer, expiresAt }) => {
    try {
      const saved = await createBudgetNote({
        accountId, note, retailer, expiresAt, jwtToken,
      });
      setNotes(prev => sortNewestFirst([...prev.filter(n => n.id !== saved.id), saved]));
      return true;
    } catch (error) {
      Notify.show('danger', 'Budget Notes', error.message || 'Unable to save note.');
      return false;
    }
  }, [accountId, jwtToken]);

  const updateNote = useCallback(async (id, changes) => {
    try {
      const saved = await updateBudgetNote({
        id, accountId, changes, jwtToken,
      });
      setNotes(prev => {
        const others = prev.filter(n => n.id !== id);
        return saved.isActive === false ? others : sortNewestFirst([...others, saved]);
      });
      return true;
    } catch (error) {
      Notify.show('danger', 'Budget Notes', error.message || 'Unable to update note.');
      return false;
    }
  }, [accountId, jwtToken]);

  return {
    notes, loading, addNote, updateNote,
  };
}
